import React, { useEffect, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { carburant2CO2 } from "../../references/glossaire_carburant2CO2";
import nuageCO2Rouge from "../../ressources/images/nuage CO2 rouge.svg";
import nuageCO2Vert from "../../ressources/images/nuage CO2 vert.svg";
import HistogramRelatif from "../../views/Graph/HistogramRelatif";
import "./CO2.css";
import { Debit } from "../../typings/types";
import { getFilteredData } from "../../ressources/tools/utils"; // Importar a função

const CO2 = ({
  mediane,
  data,
  indexSensor,
  engineeringUnitCO2,
  carburant,
  setCarburant,
}: {
  mediane: number;
  data: Debit[];
  indexSensor: number;
  engineeringUnitCO2: string;
  carburant: string;
  setCarburant: (arg0: string) => void;
}) => {
  const litreParH =
    indexSensor !== undefined && data.length > 0 && data[indexSensor]
      ? data[indexSensor].debit
      : undefined;

  const [c02ParH, setC02ParH] = useState<number | null | undefined>(undefined);
  const [hasValidData, setHasValidData] = useState(false);
  const [adjustedData, setAdjustedData] = useState<
    (Debit & { adjustedDebit?: number })[]
  >([]);
  const [adjustedMediane, setAdjustedMediane] = useState<number>(0);
  const [showPopup, setShowPopup] = useState(false);

  const isValid = (value: number | undefined | null) =>
    value != null && Number.isFinite(value);

  // Calcule le CO2/h
  useEffect(() => {
    if (litreParH && isValid(litreParH) && carburant in carburant2CO2) {
      setC02ParH(
        litreParH * carburant2CO2[carburant as keyof typeof carburant2CO2]
      );
    } else {
      setC02ParH(null);
    }
  }, [carburant, litreParH]);

  // Médiane ajustée
  // Ici nous calculons nous même la médiane car celle renvoyé du back est fausse (potentillement calculé sur les 30 derniers jours quel que soit la fréquence choisie)

  // Vérifie si les données sont valides pour l'affichage
  useEffect(() => {
    if (
      !isValid(adjustedMediane) ||
      !Array.isArray(data) ||
      !isValid(indexSensor) ||
      !isValid(litreParH) ||
      !isValid(c02ParH)
    ) {
      setHasValidData(false);
    } else {
      setHasValidData(true);
    }
  }, [data, indexSensor, litreParH, c02ParH]);

  // Recalcule adjustedData lorsque le carburant change
  useEffect(() => {
    if (data && data.length > 0 && carburant in carburant2CO2) {
      const newData = data.map((item: Debit) => {
        const adjustedDebit =
          item.debit * carburant2CO2[carburant as keyof typeof carburant2CO2];
        return { ...item, adjustedDebit };
      });

      // Calculer la moyenne des adjustedDebit
      const totalAdjustedDebit = newData.reduce(
        (sum, item) => sum + item.adjustedDebit,
        0
      );
      const adjustedMedian = totalAdjustedDebit / newData.length;

      // newData[Math.round(newData.length / 2) - 1].adjustedDebit;
      setAdjustedMediane(adjustedMedian);
      setAdjustedData(newData);
    } else {
      setAdjustedData([]);
    }
  }, [data, carburant]);

  // Utiliser la fonction importée pour obtenir filteredData et relativeIndex
  const { filteredData, relativeIndex } = getFilteredData(
    adjustedData,
    indexSensor,
    9,
    10
  );

  // Vérifie si les données sont valides
  if (!hasValidData) {
    return (
      <button className="btnMsg">
        Pas de données enregistrées pour cette période. Essayez une autre plage
        de dates !
      </button>
    );
  }

  const handleClick = () => {
    setShowPopup(true);
  };

  const displayCO2H = () => {
    const valeurParRapportALaMoyenne = c02ParH ? c02ParH - adjustedMediane : 0;
    if (c02ParH && valeurParRapportALaMoyenne === 0) {
      return (
        <>
          Cette machine est la valeur moyenne ({c02ParH.toFixed(2)}
          <span className="unitcss">{engineeringUnitCO2}</span>)
        </>
      );
    }
    return c02ParH ? (
      <>
        {valeurParRapportALaMoyenne > adjustedMediane && "+"}
        {valeurParRapportALaMoyenne.toFixed(2)}
        <span className="unitcss">{engineeringUnitCO2}</span>
      </>
    ) : (
      ""
    );
  };

  return (
    <div className="tab-content">
      <div className="config-group">
        <div className="input-group">
          <h3 className="header2">
            <div className="marginDiv">{displayCO2H()}</div>
            <div className="CarburanSelct">
              <select
                className="consoGazoleParHeureSelect"
                id="carburant-select"
                value={carburant}
                onChange={(e) => setCarburant(e.target.value)}
                data-testid="select-carburant"
              >
                {Object.keys(carburant2CO2).map((nomCarburant) => (
                  <option
                    key={nomCarburant}
                    className="consoGazoleParHeureSelectOption"
                    value={nomCarburant}
                  >
                    {nomCarburant.charAt(0).toUpperCase() +
                      nomCarburant.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </h3>

          {c02ParH && !isNaN(c02ParH) && c02ParH > adjustedMediane ? (
            <img
              src={nuageCO2Rouge}
              className="icon-label nuague"
              alt="CO2 élevé"
            />
          ) : (
            <img
              src={nuageCO2Vert}
              className="icon-label nuague"
              alt="CO2 bas"
            />
          )}

          <div className="input-group">
            <HistogramRelatif
              data={filteredData}
              mediane={adjustedMediane}
              indexSensor={relativeIndex} // Passe l'index relatif
              unit={engineeringUnitCO2}
            />
          </div>

          <button onClick={handleClick} className="conseil marginup">
            Afficher la légende
          </button>

          {showPopup && (
            <div className="backBluer">
              <div className="popup nwspopup">
                <div className="popup-content">
                  <span className="Nclose" onClick={() => setShowPopup(false)}>
                    <AiOutlineClose
                      className="close-icon"
                      aria-label="close-icon"
                    />
                  </span>
                  <img
                    src={require("../../ressources/images/PopupOn.png")}
                    className="npopup"
                    alt="Legende"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CO2;
